import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MoreHorizontal, ImageIcon } from "lucide-react";
import { ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import {
  asText,
  Breadcrumb,
  CardShell,
  DateRange,
  DateRangeDropdown,
  EmptyRow,
  formatCompact,
  formatCurrency,
  formatRoas,
  LoadingRow,
  MetricCard,
  presetToDates,
  readMetric,
  StatusBadge,
  TabsRow,
  unwrapValue,
  useCxoResource,
} from "./shared";
import AudienceTab from "./tabs/audience-tab";
import PlacementsTab from "./tabs/placements-tab";
import DevicesTab from "./tabs/devices-tab";
import { RegionMap } from "./tabs/regions-tab";
import { NO_PERFORMANCE_HEX, regionPerformance } from "../nigeria-map";
import { getDeviceIconMeta, getGenderIconMeta, IconMeta } from "./device-gender-icons";

const DONUT_COLORS = ["#4f46e5", "#60a5fa", "#f59e0b", "#a855f7"];

// Condensed mini-card reused on the Performance tab — real data, same
// endpoints the full Placements/Audience/Regions tabs use, just a smaller
// glance view rather than their own full tables/filters.
const MiniBarList: React.FC<{ title: string; items: { label: string; value: number }[] }> = ({ title, items }) => (
  <CardShell title={title}>
    {items.length === 0 ? (
      <EmptyRow label="No data yet." />
    ) : (
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-300 truncate">{it.label}</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100 shrink-0 ml-2">{it.value.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    )}
  </CardShell>
);

// `iconFor` — when given, looks up each row's raw `label` (impression_device/
// gender, before prettifying) against the real icon set (device-gender-icons.tsx)
// and swaps that icon in for the plain colored dot, using its prettified
// label instead of the raw one. Omitted entirely, this falls back to the dot
// exactly as before (nothing else calls MiniDonut without it anymore, but the
// fallback costs nothing to keep).
const MiniDonut: React.FC<{ title: string; items: { label: string; value: number }[]; iconFor?: (raw: string) => IconMeta }> = ({ title, items, iconFor }) => (
  <CardShell title={title}>
    {items.length === 0 ? (
      <EmptyRow label="No data yet." />
    ) : (
      <div className="flex items-center gap-3">
        <div className="h-20 w-20 shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={items} dataKey="value" nameKey="label" innerRadius={22} outerRadius={38} paddingAngle={2}>
                {items.map((it, i) => (
                  <Cell key={i} fill={iconFor?.(it.label).color || DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1 text-[11px] flex-1 min-w-0">
          {items.map((it, i) => {
            const meta = iconFor?.(it.label);
            return (
              <div key={i} className="flex items-center gap-1.5">
                {meta?.Icon ? (
                  <meta.Icon size={12} className="shrink-0" />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                )}
                <span className="text-gray-600 dark:text-gray-300 truncate flex-1">{meta?.label || it.label}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100 shrink-0">{it.value.toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </CardShell>
);

const PerformanceTab: React.FC<{ adId: string; baseParams: Record<string, any>; preview: any; series: any[] }> = ({ adId, baseParams, preview, series }) => {
  const { data: placements } = useCxoResource<any>("/cxo-dashboard/ads/creatives", `${adId}/placements`, baseParams, !!adId);
  const { data: devices } = useCxoResource<any>("/cxo-dashboard/ads/creatives", `${adId}/devices`, baseParams, !!adId);
  const { data: audience } = useCxoResource<any>("/cxo-dashboard/ads/creatives", `${adId}/audience`, baseParams, !!adId);
  const { data: regions } = useCxoResource<any>("/cxo-dashboard/ads/creatives", `${adId}/regions`, baseParams, !!adId);

  const placementItems = (Array.isArray(placements?.items) ? placements.items : []).map((it: any) => ({
    label: asText(it.platform_position ?? it.placement, "N/A"),
    value: Number(unwrapValue(it.results_share_pct ?? it.metrics?.results_share_pct)) || 0,
  }));
  const deviceItems = (Array.isArray(devices?.items) ? devices.items : []).map((it: any) => ({
    label: asText(it.impression_device, "N/A"),
    value: Number(unwrapValue(it.impressions_share_pct ?? it.metrics?.impressions_share_pct)) || 0,
  }));
  const genderItems = (Array.isArray(audience?.by_gender) ? audience.by_gender : []).map((g: any) => ({
    label: asText(g.gender, "N/A"),
    value: Number(unwrapValue(g.impressions_share_pct ?? g.metrics?.impressions_share_pct)) || 0,
  }));
  // Confirmed live elsewhere (campaigns/tabs/regions-tab.tsx): the real
  // per-item name field on a regional breakdown is `region`, not `name` —
  // this mini-card was reading the one field that never exists, so every row
  // showed its real share percentage next to a blank label.
  const regionRows: any[] = Array.isArray(regions?.items) ? regions.items : [];
  const regionItems = regionRows.map((r: any) => ({
    label: asText(r.region ?? r.name, "N/A"),
    hex: regionPerformance(r)?.hex ?? NO_PERFORMANCE_HEX,
    value: Number(unwrapValue(r.spend_share_pct ?? r.impressions_share_pct ?? r.metrics?.spend_share_pct ?? r.metrics?.impressions_share_pct)) || 0,
  }));

  // Per direct ask: prefer the real image first, then a video, then the
  // always-present thumbnail as a last resort — `preview` fields are read
  // defensively off `inner` too, in case this endpoint's response doesn't
  // nest them the same way the ads-list endpoint's own `preview` object does.
  const imageUrl = preview?.image_url;
  const videoUrl = preview?.video_url;
  const thumbnailUrl = preview?.thumbnail_url;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardShell title="Ad creative preview">
          <div className="rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 aspect-[4/5] flex items-center justify-center relative">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="w-full h-full object-cover" />
            ) : videoUrl ? (
              <video src={videoUrl} poster={thumbnailUrl} controls className="w-full h-full object-cover" />
            ) : thumbnailUrl ? (
              <img src={thumbnailUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon size={32} className="text-gray-300 dark:text-gray-600" />
            )}
          </div>
        </CardShell>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {/* Cards rendered by the parent above this tab already cover the
                full set — this column focuses on the daily trend instead of
                repeating them. */}
          </div>
          <CardShell title="Daily performance" description="Spend, results and cost per result over time.">
            {series.length === 0 ? (
              <EmptyRow label="No daily performance for this period yet." />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={series.map((s) => ({ date: asText(s.date), spend: unwrapValue(s.spend) || 0, results: unwrapValue(s.results) || 0, cost_per_result: unwrapValue(s.cost_per_result) || 0 }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="left" type="monotone" dataKey="spend" name="Spend" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="left" type="monotone" dataKey="results" name="Results" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line yAxisId="right" type="monotone" dataKey="cost_per_result" name="Cost per result" stroke="#d946ef" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardShell>
        </div>
      </div>

      {/* Left 3/4: the three small cards with the region map under them,
          filling what used to be empty space. Right 1/4: the region list,
          pinned to the left column's height and scrolling inside itself
          rather than stretching the whole row to 38 states' worth. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
            <MiniBarList title="Top placements" items={placementItems} />
            <MiniDonut title="Device breakdown" items={deviceItems} iconFor={getDeviceIconMeta} />
            <MiniDonut title="Gender breakdown" items={genderItems} iconFor={getGenderIconMeta} />
          </div>
          <CardShell title="Regional performance map" description="Results distribution across regions — click a marker for more detail.">
            {regionRows.length === 0 ? <EmptyRow label="No regional data yet." /> : <RegionMap items={regionRows} height={360} />}
          </CardShell>
        </div>
        <div className="relative min-h-[320px]">
          <CardShell title="Region breakdown" className="lg:absolute lg:inset-0 flex flex-col max-h-80 lg:max-h-none">
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              {regionItems.length === 0 ? (
                <EmptyRow label="No data yet." />
              ) : (
                <div className="space-y-2">
                  {regionItems.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 min-w-0 text-gray-600 dark:text-gray-300">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: it.hex }} />
                        <span className="truncate">{it.label}</span>
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100 shrink-0 ml-2">{it.value.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardShell>
        </div>
      </div>
    </div>
  );
};

// Ad creative detail — GET /cxo-dashboard/ads/creatives/{id} for the preview
// + cards + daily series, then Audience/Placements/Devices as their own
// (full, reused) tabs. No separate Regions tab here per the reference
// design's own tab row, even though the backend exposes one — its data
// still surfaces as the Performance tab's "Region breakdown" mini-card.
const AdCreativeDetail: React.FC = () => {
  const { campaignId, adsetId, adId } = useParams<{ campaignId: string; adsetId: string; adId: string }>();
  const [dateRange, setDateRange] = useState<DateRange>("Last 7 Days");
  const { start_date, end_date } = useMemo(() => presetToDates(dateRange), [dateRange]);
  const baseParams = useMemo(() => ({ mode: "fixed", start_date, end_date }), [start_date, end_date]);
  const [tab, setTab] = useState("performance");

  const { data, loading } = useCxoResource<any>("/cxo-dashboard/ads/creatives", adId, { ...baseParams, granularity: "daily" }, !!adId);

  if (loading && !data) return <LoadingRow label="Loading ad creative…" />;
  const inner = data?.data || data;
  if (!inner) return <EmptyRow label="Couldn't load this ad creative." />;

  const name = inner.name ?? inner.title;
  const status = inner.status;
  // Falls back to reading image_url/video_url/thumbnail_url straight off
  // `inner` in case this single-ad endpoint doesn't nest them under `preview`
  // the same way the ads-list endpoint does (confirmed live there) — harmless
  // either way since inner.preview wins whenever it's actually present.
  const preview = inner.preview || { image_url: inner.image_url, video_url: inner.video_url, thumbnail_url: inner.thumbnail_url };
  const series: any[] = Array.isArray(inner.series) ? inner.series : [];
  const s = inner.summary || {};

  const tabs = [
    { id: "performance", label: "Performance" },
    { id: "audience", label: "Audience" },
    { id: "placements", label: "Placements" },
    { id: "devices", label: "Devices" },
  ];

  return (
    <div className="space-y-5">
      <Breadcrumb
        trail={[
          { label: "Campaigns", to: "/cxo/campaigns" },
          { label: inner.campaign?.name || "Campaign", to: campaignId ? `/cxo/campaigns/${campaignId}` : undefined },
          { label: inner.adset?.name || "Ad set", to: campaignId && adsetId ? `/cxo/campaigns/${campaignId}/adsets/${adsetId}` : undefined },
          { label: name || "Ad" },
        ]}
      />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{name}</h1>
          {status && <StatusBadge status={status} />}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DateRangeDropdown value={dateRange} onChange={setDateRange} />
          <button type="button" className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800">
            <MoreHorizontal size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <MetricCard label="Spend" metric={readMetric(s.spend)} format={formatCurrency} invert empty="₦0.00" />
        <MetricCard label="Impressions" metric={readMetric(s.impressions)} format={formatCompact} empty="0" />
        <MetricCard label="Reach" metric={readMetric(s.reach)} format={formatCompact} empty="0" />
        <MetricCard label="Results" metric={readMetric(s.results)} format={formatCompact} empty="0" />
        <MetricCard label="Cost per result" metric={readMetric(s.cost_per_result)} format={formatCurrency} invert empty="₦0.00" />
        <MetricCard label="Reported value" metric={readMetric(s.reported_value ?? s.attributed_revenue)} format={formatCurrency} empty="₦0.00" />
        <MetricCard label="ROAS" metric={readMetric(s.roas)} format={formatRoas} empty="N/A" />
      </div>

      <TabsRow tabs={tabs} active={tab} onChange={setTab} />

      {adId && (
        <>
          {tab === "performance" && <PerformanceTab adId={adId} baseParams={baseParams} preview={preview} series={series} />}
          {tab === "audience" && <AudienceTab basePath="/cxo-dashboard/ads/creatives" entityId={adId} baseParams={baseParams} />}
          {tab === "placements" && <PlacementsTab basePath="/cxo-dashboard/ads/creatives" entityId={adId} baseParams={baseParams} exportPath={`/cxo-dashboard/ads/creatives/${adId}/placements/export`} />}
          {tab === "devices" && <DevicesTab basePath="/cxo-dashboard/ads/creatives" entityId={adId} baseParams={baseParams} />}
        </>
      )}
    </div>
  );
};

export default AdCreativeDetail;
